import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { AppearancePanel, Badge, Button, Card, Heading, Input, StatusDot, Text } from '@diwata/ironvale'
import { useUI } from '@/store/ui'
import { AuthAPI } from '@/api/auth'
import { DiwaAPI } from '@/api/client'
import { HelpHint } from '@/components/HelpHint'
import { FlowDiagram } from '@/components/FlowDiagram'
import { useOnboardingReentry } from '@/components/Onboarding'
import './settings.css'

// Settings — one calm place for everything about your account: who you are, how the
// app looks, your familiar Tala, where your model runs, your plan, and the developer
// escape hatches. Section titles are real ironvale Headings so the page has a legible
// spine; copy stays in plain words (no "house key", no "bid profile", no server codes).

/** Mirrors GET /agent/model-key — reports whether your own model key is wired.
 *  Never carries the key itself, only a last-4 and liveness flags. */
interface KeyState {
  connected: boolean
  provider: string
  last4: string | null
  connected_at: string | null
  needs_reentry: boolean
}

/**
 * Model access — the sovereignty ladder from the familiar spec, made concrete.
 * Three rungs: Diwata runs the model for you (default), you bring your own key
 * (works today, reuses the /agent/model-key endpoint the Familiar already uses),
 * or one day it runs on your own machine. The middle rung is the only live control;
 * the copy never says "house key" or other backstage words.
 */
function ModelAccess() {
  const { diwaUrl } = useUI()
  const api = useMemo(() => new DiwaAPI(diwaUrl), [diwaUrl])
  const qc = useQueryClient()

  const { data: key, isLoading, isError } = useQuery({
    queryKey: ['model-key'],
    queryFn: () => api.get<KeyState>('/agent/model-key'),
  })

  const [draft, setDraft] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const onYourKey = Boolean(key?.connected)

  async function connect() {
    if (!draft.trim() || busy) return
    setBusy(true)
    setError(null)
    try {
      await api.put('/agent/model-key', { key: draft.trim() })
      setDraft('')
      await qc.invalidateQueries({ queryKey: ['model-key'] })
    } catch (err) {
      const status = (err as { status?: number }).status
      if (status === 400) setError("That doesn't look like a Claude key (it should start with sk-ant-…) — nothing was saved.")
      else if (status === 503) setError("This server can't hold your key yet — nothing was saved.")
      else setError("Couldn't save the key — try again.")
    } finally {
      setBusy(false)
    }
  }

  async function disconnect() {
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      await api.del('/agent/model-key')
      await qc.invalidateQueries({ queryKey: ['model-key'] })
    } catch {
      setError("Couldn't remove the key — try again.")
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card>
      <div className="dw-settings-head">
        <Heading level={3}>
          Model access <HelpHint gloss="Which model does the thinking for your familiar, and whose account pays for it." />
        </Heading>
      </div>
      <p className="dw-settings-sub">
        Your familiar Tala runs on a large language model. You choose whose — from us running it for
        you, up to running it entirely on your own terms.
      </p>

      <div className="dw-ladder">
        {/* Rung 1 — Diwata provides the model. The default when no key is set. */}
        <div className={`dw-ladder__rung${!onYourKey ? ' dw-ladder__rung--active' : ''}`}>
          <div className="dw-ladder__head">
            <span className="dw-ladder__name">
              <StatusDot state={!onYourKey ? 'live' : 'neutral'} size="sm" label="Diwata-provided model access" />
              Provided by Diwata
            </span>
            <Badge variant={!onYourKey ? 'success' : 'neutral'}>{!onYourKey ? 'In use' : 'Available'}</Badge>
          </div>
          <div className="dw-ladder__desc">
            The simplest path: we run the model and your familiar just works. Nothing to set up. Your
            plan sets how much it can do each month.
          </div>
        </div>

        {/* Rung 2 — your own key. The one live control on this page. */}
        <div className={`dw-ladder__rung${onYourKey ? ' dw-ladder__rung--active' : ''}`}>
          <div className="dw-ladder__head">
            <span className="dw-ladder__name">
              <StatusDot
                state={onYourKey ? (key?.needs_reentry ? 'idle' : 'live') : 'neutral'}
                size="sm"
                label="Your own model key"
              />
              Your own key
            </span>
            {!isLoading && !isError && (
              <Badge variant={onYourKey ? (key?.needs_reentry ? 'info' : 'success') : 'neutral'}>
                {onYourKey ? (key?.needs_reentry ? 'Re-enter key' : 'In use') : 'Bring your key'}
              </Badge>
            )}
          </div>

          {isError ? (
            <div className="dw-settings-error">Couldn't check your key — reload to try again.</div>
          ) : onYourKey ? (
            <>
              {key?.needs_reentry ? (
                <div className="dw-ladder__desc">
                  Your saved key (ends …{key.last4}) can't be read any more, so we've fallen back to the
                  Diwata-provided model. Remove it and paste the key again to switch back.
                </div>
              ) : (
                <div className="dw-ladder__desc">
                  Connected — key ending <span style={{ fontFamily: 'var(--ae-font-mono)' }}>…{key?.last4}</span>.
                  Tala runs on your Claude account. Kept encrypted; only ever sent to Anthropic.
                </div>
              )}
              <div className="dw-ladder__control">
                <Button size="sm" variant="ghost" disabled={busy} onClick={disconnect}>Remove key</Button>
              </div>
            </>
          ) : (
            <>
              <div className="dw-ladder__desc">
                Have a Claude (Anthropic) API key? Paste it and Tala runs on your account instead of ours.
                Stored encrypted, shown only as the last 4 characters, and never sent anywhere but Anthropic.
              </div>
              <div className="dw-ladder__control">
                <input
                  className="dw-input"
                  type="password"
                  placeholder="sk-ant-…"
                  value={draft}
                  aria-label="Your Claude API key"
                  autoComplete="off"
                  data-1p-ignore
                  data-lpignore="true"
                  onChange={(e) => setDraft(e.target.value)}
                />
                <Button size="sm" variant="primary" disabled={busy || !draft.trim()} onClick={connect}>
                  {busy ? 'Saving…' : 'Connect'}
                </Button>
              </div>
            </>
          )}
          {error && <div className="dw-settings-error">{error}</div>}
        </div>

        {/* Rung 3 — fully local. Not built yet; described so the ladder reads whole. */}
        <div className="dw-ladder__rung">
          <div className="dw-ladder__head">
            <span className="dw-ladder__name">
              <StatusDot state="neutral" size="sm" label="Model on your own machine" />
              On your own machine
            </span>
            <span className="dw-chip">Coming soon</span>
          </div>
          <div className="dw-ladder__desc">
            The far end of the ladder: run a model on hardware you control, so nothing leaves your machine.
            We're building toward it.
          </div>
        </div>
      </div>
    </Card>
  )
}

export function Settings() {
  const { diwaUrl, gateUrl, token, updateSettings, setToken } = useUI()
  const navigate = useNavigate()
  const [form, setForm] = useState({ diwaUrl, gateUrl })
  const [saved, setSaved] = useState(false)
  const [advancedOpen, setAdvancedOpen] = useState(false)

  const replayWelcome = useOnboardingReentry()

  const { data: me } = useQuery({
    queryKey: ['auth-me'],
    queryFn: () => new AuthAPI(diwaUrl).me(),
    enabled: !!token,
  })

  function save() {
    updateSettings(form)
    setSaved(true)
    setTimeout(() => setSaved(false), 1500)
  }

  function signOut() {
    setToken(null)
    navigate('/login')
  }

  return (
    <div>
      <div className="dw-page-header">
        <div className="dw-page-title">
          Settings <HelpHint gloss="Your account, how the app looks, your familiar, your model, and your plan — all in one place." /> <FlowDiagram />
        </div>
        <div className="dw-page-subtitle">Everything about your account and how Diwa works for you.</div>
      </div>

      <div className="dw-settings">
        {/* ── Account ─────────────────────────────────────────────────────── */}
        <Card>
          <div className="dw-settings-head">
            <Heading level={3}>Account</Heading>
            <Button variant="ghost" size="sm" onClick={signOut}>Sign out</Button>
          </div>
          <div className="dw-settings-row">
            <div className="dw-settings-row__body">
              <div className="dw-settings-row__title">{me?.email ?? '…'}</div>
              <div className="dw-settings-row__desc">
                Signed in{me?.email_verified ? ' · email verified' : ''}
              </div>
            </div>
          </div>
        </Card>

        {/* ── Appearance ──────────────────────────────────────────────────── */}
        <Card>
          <div className="dw-settings-head">
            <Heading level={3}>Appearance</Heading>
          </div>
          <p className="dw-settings-sub">Theme, accent, and text size. Changes apply everywhere, right away.</p>
          <AppearancePanel align="left" />
        </Card>

        {/* ── Your familiar & onboarding ──────────────────────────────────── */}
        <Card>
          <div className="dw-settings-head">
            <Heading level={3}>Your familiar &amp; welcome</Heading>
          </div>
          <p className="dw-settings-sub">
            Tala is your familiar — the guide who helps you set up and answers questions about your world.
          </p>

          <div className="dw-settings-row">
            <div className="dw-settings-row__body">
              <div className="dw-settings-row__title">Replay the welcome</div>
              <div className="dw-settings-row__desc">Start again with Tala's guided setup, from the top.</div>
            </div>
            <Button variant="ghost" size="sm" onClick={replayWelcome}>Start again with Tala</Button>
          </div>

          <div className="dw-settings-row">
            <div className="dw-settings-row__body">
              <div className="dw-settings-row__title">About you</div>
              <div className="dw-settings-row__desc">
                What Tala knows about you — your name, what you do, and what you're working toward —
                lives on your Profile, alongside your sigils and progression.
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/profile')}>Open your Profile</Button>
          </div>

          <div className="dw-settings-row">
            <div className="dw-settings-row__body">
              <div className="dw-settings-row__title">Talk to Tala</div>
              <div className="dw-settings-row__desc">Open the familiar full-screen to ask or draft anything.</div>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/familiar')}>Open Tala</Button>
          </div>
        </Card>

        {/* ── Model access ────────────────────────────────────────────────── */}
        <ModelAccess />

        {/* ── Plan & billing ──────────────────────────────────────────────── */}
        <Card>
          <div className="dw-settings-head">
            <Heading level={3}>Plan &amp; billing</Heading>
          </div>
          <p className="dw-settings-sub">
            Your plan sets how many domains and missions you get, and which sources you can pull from.
          </p>
          <div className="dw-settings-row">
            <div className="dw-settings-row__body">
              <div className="dw-settings-row__title">Plans</div>
              <div className="dw-settings-row__desc">Compare tiers and what each one unlocks.</div>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/pricing')}>View plans</Button>
          </div>
          <div className="dw-settings-row">
            <div className="dw-settings-row__body">
              <div className="dw-settings-row__title">Invoices &amp; credits</div>
              <div className="dw-settings-row__desc">Receipts and remaining credit will live here.</div>
            </div>
            <span className="dw-chip">Coming soon</span>
          </div>
        </Card>

        {/* ── Advanced ────────────────────────────────────────────────────── */}
        <Card>
          <button
            onClick={() => setAdvancedOpen((v) => !v)}
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', width: '100%' }}
            aria-expanded={advancedOpen}
          >
            <div className="dw-settings-head" style={{ marginBottom: 0 }}>
              <Heading level={3}>Advanced</Heading>
              <span style={{ fontSize: 'calc(13px * var(--ae-text-scale, 1))', color: 'var(--ae-color-text-secondary)' }}>
                {advancedOpen ? '▾' : '▸'}
              </span>
            </div>
          </button>
          {advancedOpen && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--ae-space-component-md)', marginTop: 12 }}>
              <div className="dw-settings-error" style={{ color: 'var(--ae-color-feedback-warning)' }}>
                Developer settings — changing these can break your session.
              </div>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--ae-space-1)' }}>
                <Text variant="label">Diwa API URL</Text>
                <Input value={form.diwaUrl} onChange={(e) => setForm({ ...form, diwaUrl: e.target.value })} placeholder="/api/diwa" />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--ae-space-1)' }}>
                <Text variant="label">Sign-in service URL</Text>
                <Input value={form.gateUrl} onChange={(e) => setForm({ ...form, gateUrl: e.target.value })} placeholder="http://localhost:8004" />
              </label>
              <div>
                <Button variant="primary" size="sm" onClick={save}>{saved ? 'Saved' : 'Save'}</Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  )
}
